/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState } from 'react';

import { passportApi } from '@coze-foundation/account-adapter';
import { Toast } from '@coze-arch/coze-design';

interface WalletInfo {
  balance: number;
  mode: 'token' | 'coin';
  low_balance_warning?: boolean;
  unlimited?: boolean;
}

/**
 * variant="sidebar" — compact vertical display for narrow rails
 * variant="subMenu" — wallet card pinned to the bottom of the submenu panel
 * variant="header"  — horizontal display for page headers
 */
export const WalletBadge = ({
  variant = 'sidebar',
}: {
  variant?: 'sidebar' | 'subMenu' | 'header';
}) => {
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const shownWarningRef = useRef(false);

  useEffect(() => {
    const fetch = () =>
      passportApi
        .getWalletInfo()
        .then(setWalletInfo)
        .catch(() => {
          /* non-fatal */
        });

    fetch();
    const timer = setInterval(fetch, 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (walletInfo?.low_balance_warning) {
      if (!shownWarningRef.current) {
        shownWarningRef.current = true;
        Toast.warning({
          content:
            walletInfo.balance === 0
              ? 'Your wallet balance is 0. Please top up to continue using the service.'
              : 'Your wallet balance is running low.',
          duration: 5,
          showClose: true,
        });
      }
    } else {
      // Reset so it shows again next time warning triggers
      shownWarningRef.current = false;
    }
  }, [walletInfo?.low_balance_warning, walletInfo?.balance]);

  if (!walletInfo) {
    return null;
  }

  const label = walletInfo.mode === 'token' ? 'tokens' : 'coins';
  const balance = walletInfo.unlimited
    ? '∞'
    : walletInfo.balance.toLocaleString();

  if (variant === 'header') {
    return (
      <div className="flex items-center gap-1 text-[13px] coz-fg-secondary select-none">
        <span className="font-[500] coz-fg-primary">{balance}</span>
        <span>{label}</span>
      </div>
    );
  }

  if (variant === 'subMenu') {
    return (
      <div className="w-full select-none rounded-[16px] border-2 border-[#E8A52A] bg-[linear-gradient(180deg,rgba(255,255,255,0)_-0.64%,#FFF1D3_99.36%)] px-[16px] py-[14px] shadow-[0_0_0_1px_rgba(244,193,77,0.28),inset_0_1px_0_rgba(255,255,255,0.9)]">
        <div className="flex items-center gap-1 text-[14px] font-[700] leading-[20px] text-[#1d1c23]">
          <span className="text-[15px] text-[#F2A81D]" aria-hidden="true">
            ◎
          </span>
          <span>
            {balance} {label}
          </span>
        </div>
      </div>
    );
  }

  // sidebar variant — compact card styled to match the left-rail footer
  return (
    <div className="mb-[4px] w-[88px] select-none rounded-[14px] border border-[#F4C14D] bg-[linear-gradient(180deg,#FFFDF9_0%,#FFF1D3_100%)] px-[8px] py-[8px] text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
      <div className="flex items-center justify-center gap-1 leading-tight">
        <span
          className="text-[12px] font-[700] text-[#F2A81D]"
          aria-hidden="true"
        >
          ◎
        </span>
        <span className="text-[11px] font-[700] text-[#1d1c23]">
          {balance} {label}
        </span>
      </div>
    </div>
  );
};
